import styles from "./index.module.css";
import React, { useState } from "react";
import { Ok } from "react-ikonate";

import Table from "shared/Table";
import Button, {
  ButtonPurpose,
  ButtonSize,
  ConfirmButton,
} from "shared/Button";
import Loader from "shared/Loader";
import TabActionBar from "shared/TabActionBar";

import AddressEdit from "./AddressEdit";
import AddressLabel from "./AddressLabel";

import { useAddresses } from "./useAddresses";
import { Auth } from "shared/@auth/Auth";
import { Form } from "shared/@forms/Form";
import createNotification from "shared/@utils/createNotification";

export default function Addresses({ iboNumber }) {
  const { hasPermission } = Auth.useContainer();
  const {
    addresses,
    saveAddress,
    deleteAddress,
    verifyAddress,
    addressOptions,
    isWaiting,
  } = useAddresses(iboNumber);
  const [editData, setEditData] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canUpdate = hasPermission("membership.ibo.update");

  return (
    <div className={styles.container}>
      {canUpdate && (
        <TabActionBar>
          <Button
            onClick={() => {
              setEditData(createNewAddressData());
            }}
            disabled={editData != null}
          >
            Add Address
          </Button>
        </TabActionBar>
      )}

      {addresses == null || isWaiting ? (
        <Loader />
      ) : (
        <>
          {addresses.length === 0 ? (
            <span>IBO has no addresses on file.</span>
          ) : (
            <Table>
              <thead>
                <tr>
                  {canUpdate && <th>Edit</th>}
                  <th>Primary</th>
                  <th>Default Shipping</th>
                  <th>Address</th>
                  {canUpdate && <th style={{ textAlign: "right" }}>Remove</th>}
                </tr>
              </thead>
              <tbody>
                {addresses.map((a) => {
                  const isPrimary = (a.type & 1) === 1;
                  const isShipping = (a.type & 4) === 4;
                  const isBeingEdited =
                    editData != null && a.addressId === editData.addressId;

                  return (
                    <tr key={a.iboAddressId} className={styles.resultRow}>
                      {canUpdate && (
                        <td>
                          <Button
                            size={ButtonSize.small}
                            disabled={isBeingEdited}
                            onClick={() => {
                              setEditData(a);
                            }}
                          >
                            Edit
                          </Button>
                        </td>
                      )}

                      <td>{isPrimary ? <YesLabel /> : "No"}</td>
                      <td>{isShipping ? <YesLabel /> : "No"}</td>
                      <td>
                        <AddressLabel
                          address={a}
                          addressOptions={addressOptions}
                        />
                      </td>

                      {canUpdate && (
                        <td align="right">
                          {isPrimary ? (
                            <span className={styles.textFaded}>
                              Cannot Remove Primary
                            </span>
                          ) : (
                            <ConfirmButton
                              purpose={ButtonPurpose.delete}
                              size={ButtonSize.small}
                              confirmText="Are you sure you want to remove this address from the IBO's account?"
                              onConfirm={() => {
                                setIsDeleting(true);

                                return deleteAddress(a.iboAddressId).finally(
                                  () => {
                                    setIsDeleting(false);
                                    setEditData(null);

                                    createNotification({
                                      type: "success",
                                      title: "Address Removed",
                                      message:
                                        "Address was removed from IBO's account.",
                                    });
                                  }
                                );
                              }}
                            >
                              Remove
                            </ConfirmButton>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {editData && (
            <AddressEdit
              data={editData}
              disabled={isUpdating}
              addressOptions={addressOptions}
              verifyAddress={verifyAddress}
              onSubmit={(address) => {
                setIsUpdating(true);

                saveAddress(address)
                  .then(() => {
                    setEditData(null);
                  })
                  // .catch((err) => {
                  //   console.error(err);
                  // })
                  .finally(() => {
                    setIsUpdating(false);
                  });
              }}
              onCancel={() => {
                setEditData(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function YesLabel() {
  return (
    <div className={styles.yesLabel}>
      <Ok className={styles.yesLabelIcon} />
      <span>Yes</span>
    </div>
  );
}

function createNewAddressData() {
  return {
    addressId: null,
    city: "",
    country: "US",
    line1: "",
    line2: null,
    postalCode: "",
    state: "WA",
    iboAddressId: null,
  };
}
